import { useCallback, useState } from 'react';
import { Logger } from '@saviynt/common';

import { useUpdateServiceIdentityMutation } from '../data-layer/serviceIdentityApi';

/**
 * Fetches service identity details for a given service ID.
 *
 * @param serviceId - The ID of the service identity to fetch details for.
 * @returns An object containing `handleUpdateIdentity`, `isSaving`, and `isSaveError`.
 */
const useUpdateServiceIdentity = (serviceId: number | undefined) => {
  const [isSaving, setIsSaving] = useState(false);

  // API hooks
  const [updateIdentity, { isError: isSaveError }] =
    useUpdateServiceIdentityMutation();

  const handleUpdateIdentity = useCallback(
    async (data: {
      description?: string | null;
      tags?: string[] | null;
      passwordPolicy?: number | null;
    }) => {
      setIsSaving(true);

      try {
        if (!serviceId) {
          throw new Error('No service ID provided');
        }

        const controller = updateIdentity({ serviceId, data });

        const response = await controller.unwrap();

        return response;
      } catch (error) {
        Logger.log('Failed to save summary', error);

        return 'Failed to get save service identity';
      } finally {
        setIsSaving(false);
      }
    },
    [updateIdentity, serviceId]
  );

  return {
    handleUpdateIdentity,
    isSaveError,
    isSaving,
  };
};

// eslint-disable-next-line import/prefer-default-export
export { useUpdateServiceIdentity };
